import { getLocationName } from 'entities-next/common'
import { OrganizationFoundedAt } from 'entities-next/scoutingCampaignCriteria/scoutingCampaignCriteria.types'
import { organizationStageToOrganizationStageLabel } from 'entities-next/search-engine/stage.utils'
import React from 'react'
import { Analysis, DisplayableAssessment } from 'scenes/Dashboard/data/analyses/analyses.types'

import { Table, TableBody, TableHead } from '@mui/material'

import { AssessmentRow } from './components/AssessmentRow'

export const AssessmentTable = React.memo(({ tableTitle, analysis }: AssessmentTableProps) => {
  if (!analysis) {
    return null
  }
  const { positiveDisplayableAssessments, negativeDisplayableAssessments } = analysisToDisplayableAssessment(analysis)
  return (
    <Table size="small">
      <TableHead>
        <AssessmentRow headerTitle={tableTitle} analysisScore={analysis.score.compatibility} />
      </TableHead>
      <TableBody>
        {positiveDisplayableAssessments.map((assessment, index) => {
          return (
            <AssessmentRow
              key={`${index}-${assessment}`}
              label={assessment.label}
              value={assessment.result}
              isNegativeAssessment={false}
            />
          )
        })}
        {negativeDisplayableAssessments.map((assessment, index) => {
          return (
            <AssessmentRow
              key={`${index}-${assessment}`}
              label={assessment.label}
              value={assessment.result}
              isNegativeAssessment={true}
            />
          )
        })}
      </TableBody>
    </Table>
  )
})

export function analysisToDisplayableAssessment(analysis: Analysis) {
  const { positiveAssessments, negativeAssessments } = analysis

  const positiveDisplayableAssessments: DisplayableAssessment[] = []
  const negativeDisplayableAssessments: DisplayableAssessment[] = []

  if (positiveAssessments.organizationFoundedAt) {
    positiveDisplayableAssessments.push({
      label: rangeToLabel(positiveAssessments.organizationFoundedAt),
      result: positiveAssessments.organizationFoundedAt.result,
    })
  }

  if (positiveAssessments.organizationHeadquarters) {
    const locations = positiveAssessments.organizationHeadquarters.locations.map(getLocationName).join(', ')
    positiveDisplayableAssessments.push({
      label: `Headquarters location ${locations}`,
      result: positiveAssessments.organizationHeadquarters.result,
    })
  }

  if (positiveAssessments.organizationStages) {
    const stages = positiveAssessments.organizationStages.stages
      .map(organizationStageToOrganizationStageLabel)
      .join(', ')
    positiveDisplayableAssessments.push({
      label: `Last funding event is in ${stages}`,
      result: positiveAssessments.organizationStages.result,
    })
  }

  if (negativeAssessments.negativeOrganizationHeadquarters) {
    const locations = negativeAssessments.negativeOrganizationHeadquarters.locations.map(getLocationName).join(', ')

    negativeDisplayableAssessments.push({
      label: `Headquarters location cannot be ${locations}`,
      result: negativeAssessments.negativeOrganizationHeadquarters.result,
    })
  }

  return {
    positiveDisplayableAssessments,
    negativeDisplayableAssessments,
  }
}

AssessmentTable.displayName = 'AssessmentTable'

export type AssessmentTableProps = {
  onlyHeader?: boolean
  tableTitle?: string
  analysis: Analysis
}

export function rangeToLabel({
  minimumYear,
  maximumYear,
}: Pick<OrganizationFoundedAt, 'minimumYear' | 'maximumYear'>): string {
  if (!maximumYear && minimumYear) {
    return `Organization founded on or after ${minimumYear}`
  }
  if (maximumYear && !minimumYear) {
    return `Organization founded on or before ${maximumYear}`
  }
  return `Organization founded on or after ${minimumYear} and founded on or before ${maximumYear}`
}
